import { versionColor, kindColor } from './colors.js';

// Virtualized event log — keeps DOM small even under high event rate.
// `container` scrolls; we render only the visible slice + a small buffer.

const ROW_HEIGHT = 22;
const OVERSCAN = 8;

export function createEventLog(container, opts) {
  const list = document.createElement('div');
  list.style.position = 'relative';
  list.style.width = '100%';
  container.appendChild(list);

  let events = [];
  let selectedId = null;
  let onSelect = opts?.onSelect ?? (() => {});
  let stickToTail = true;
  // Track a rolling maximum so bar widths stay proportional as traffic scales.
  let maxBytes = 1;

  container.addEventListener('scroll', () => {
    // If user scrolled up, stop sticking to the tail.
    const atBottom = container.scrollTop + container.clientHeight >= list.offsetHeight - 4;
    stickToTail = atBottom;
    render();
  });

  function pushEvents(newEvents) {
    if (!newEvents.length) return;
    events.push(...newEvents);
    // Keep an upper bound.
    if (events.length > 3000) events.splice(0, events.length - 3000);
    // Recompute rolling max against the retained events (drop stale outliers).
    let m = 1;
    for (const ev of events) if (ev.bytesOverWire > m) m = ev.bytesOverWire;
    maxBytes = m;
    list.style.height = events.length * ROW_HEIGHT + 'px';
    if (stickToTail) {
      // Scroll to the bottom before render so visible slice is correct.
      requestAnimationFrame(() => {
        container.scrollTop = list.offsetHeight;
        render();
      });
    } else {
      render();
    }
  }

  function clear() {
    events = [];
    maxBytes = 1;
    list.style.height = '0px';
    list.innerHTML = '';
  }

  function setSelected(id) {
    selectedId = id;
    render();
  }

  function render() {
    const scrollTop = container.scrollTop;
    // Fallback: if the container isn't laid out yet (0 height) still render a
    // sensible number of rows so pushed events don't disappear into a
    // zero-width viewport.
    const viewH = container.clientHeight || (events.length * ROW_HEIGHT) || 400;
    const startIdx = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
    const endIdx = Math.min(events.length, Math.ceil((scrollTop + viewH) / ROW_HEIGHT) + OVERSCAN);

    // Prepare row nodes keyed by event id.
    const existing = new Map();
    for (const child of list.children) existing.set(child.dataset.evid, child);

    const wanted = new Set();
    for (let i = startIdx; i < endIdx; i++) {
      const ev = events[i];
      if (!ev) continue;
      if (wanted.has(ev.id)) continue; // defensive against duplicate ids
      wanted.add(ev.id);
      let row = existing.get(ev.id);
      if (!row) {
        row = renderRow(ev);
        list.appendChild(row);
      }
      row.style.top = i * ROW_HEIGHT + 'px';
      row.classList.toggle('selected', ev.id === selectedId);
      // Keep bar width proportional to the current rolling max.
      const bar = row.querySelector('.sizebar > div');
      if (bar) bar.style.width = barWidthPct(ev.bytesOverWire) + '%';
    }
    // Remove out-of-view rows.
    for (const [id, node] of existing) {
      if (!wanted.has(id)) node.remove();
    }
  }

  function renderRow(ev) {
    const row = document.createElement('div');
    row.className = 'event-row';
    row.dataset.evid = ev.id;
    row.style.position = 'absolute';
    row.style.left = '0';
    row.style.right = '0';
    row.style.height = ROW_HEIGHT + 'px';
    if (ev.dictAdvertised) row.classList.add('dict-advert');

    const ts = document.createElement('div'); ts.className = 'ts'; ts.textContent = fmtTime(ev.ts);
    const user = document.createElement('div'); user.className = 'user'; user.textContent = ev.userId;
    const url = document.createElement('div'); url.className = 'url'; url.textContent = ev.url + ' @v' + ev.currentVersion;
    const sizebar = document.createElement('div'); sizebar.className = 'sizebar';
    const bar = document.createElement('div');
    bar.style.width = barWidthPct(ev.bytesOverWire) + '%';
    bar.style.background = kindColor(ev.kind);
    bar.style.opacity = ev.kind === 'cache' ? 0.6 : 1;
    sizebar.appendChild(bar);
    // For deltas, gradient background across the bar.
    if (ev.kind === 'delta' && ev.delta) {
      const from = versionColor(ev.assetId, ev.delta.fromVersion);
      const to = versionColor(ev.assetId, ev.delta.toVersion);
      bar.style.background = `linear-gradient(90deg, ${from}, ${to})`;
    } else if (ev.kind === 'full') {
      bar.style.background = versionColor(ev.assetId, ev.currentVersion);
    }
    const kind = document.createElement('div'); kind.className = 'kind ' + ev.kind;
    const kindLabel = ev.kind === 'revalidated' ? '304' : ev.kind;
    kind.textContent = kindLabel + (ev.bytesOverWire ? ` ${fmtBytes(ev.bytesOverWire)}` : ' cached');
    const badges = document.createElement('div'); badges.className = 'badges';
    if (ev.dictAdvertised) {
      const b = document.createElement('span'); b.className = 'badge dict'; b.textContent = 'DICT'; badges.appendChild(b);
    }
    if (ev.failure) {
      const b = document.createElement('span'); b.className = 'badge fail'; b.textContent = failureShort(ev.failure); badges.appendChild(b);
      row.classList.add('flash-fail');
      // Remove flash class after animation.
      setTimeout(() => row.classList.remove('flash-fail'), 900);
    }

    row.appendChild(ts);
    row.appendChild(user);
    row.appendChild(url);
    row.appendChild(sizebar);
    row.appendChild(kind);
    row.appendChild(badges);

    row.addEventListener('click', () => { selectedId = ev.id; onSelect(ev); render(); });
    return row;
  }

  // Linear proportional width against the rolling maximum. A cache hit
  // (0 bytes) still gets a minimum sliver so the color is visible.
  function barWidthPct(bytes) {
    if (bytes <= 0) return 1;
    const pct = (bytes / maxBytes) * 100;
    return Math.max(1, Math.min(100, pct));
  }

  return { pushEvents, clear, setSelected, render };
}

function fmtTime(t) {
  const s = t.toFixed(2);
  return s + 's';
}
export function fmtBytes(n) {
  if (n < 1024) return n + 'B';
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + 'KB';
  return (n / 1024 / 1024).toFixed(2) + 'MB';
}
function failureShort(f) {
  return ({
    'dict-hash-mismatch': 'HASH',
    'dict-evicted-at-client': 'EVICT',
    'delta-miss-dict-hit': 'REGEN',
    'unsupported-encoding': 'NO-DCB',
  })[f] || f;
}
